export default function AdPricingTable() {
  const pricingData = [
    {
      zone: 'Homepage Hero',
      dimensions: '970x250',
      impressions: '60K-70K',
      price: '$600',
      features: ['Prime visibility', 'Above the fold', 'Desktop only']
    },
    {
      zone: 'Sidebar Top',
      dimensions: '300x250',
      impressions: '45K-55K',
      price: '$500',
      features: ['All pages', 'High visibility', 'Desktop & tablet']
    },
    {
      zone: 'Header Banner',
      dimensions: '728x90',
      impressions: '40K-50K',
      price: '$450',
      features: ['All pages', 'Top placement', 'Desktop only']
    },
    {
      zone: 'News Sidebar',
      dimensions: '300x600',
      impressions: '35K-45K',
      price: '$450',
      features: ['News section', 'Tall format', 'High engagement']
    },
    {
      zone: 'Sidebar Middle',
      dimensions: '300x250',
      impressions: '40K-50K',
      price: '$400',
      features: ['All pages', 'Good visibility', 'Desktop & tablet']
    },
    {
      zone: 'Directory Top',
      dimensions: '728x90',
      impressions: '35K-45K',
      price: '$400',
      features: ['Directory pages', 'Business audience', 'Desktop only']
    },
    {
      zone: 'In-Content',
      dimensions: '336x280',
      impressions: '30K-40K',
      price: '$350',
      features: ['Between content', 'Native feel', 'All devices']
    },
    {
      zone: 'Events Banner',
      dimensions: '728x90',
      impressions: '30K-40K',
      price: '$350',
      features: ['Events section', 'Engaged users', 'Desktop only']
    },
    {
      zone: 'Directory Between',
      dimensions: '300x250',
      impressions: '25K-35K',
      price: '$300',
      features: ['Between listings', 'Business focus', 'All devices']
    },
    {
      zone: 'Mobile Sticky',
      dimensions: '320x50',
      impressions: '20K-30K',
      price: '$250',
      features: ['Mobile only', 'Always visible', 'High CTR']
    },
    {
      zone: 'Footer Banner',
      dimensions: '728x90',
      impressions: '15K-25K',
      price: '$200',
      features: ['All pages', 'Bottom placement', 'Desktop only']
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Pricing Overview</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Transparent monthly pricing with no hidden fees. All placements include analytics and support.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
                  <th className="px-6 py-4 text-left font-semibold">Ad Placement</th>
                  <th className="px-6 py-4 text-left font-semibold">Dimensions</th>
                  <th className="px-6 py-4 text-left font-semibold">Monthly Impressions</th>
                  <th className="px-6 py-4 text-left font-semibold">Features</th>
                  <th className="px-6 py-4 text-right font-semibold">Monthly Price</th>
                </tr>
              </thead>
              <tbody>
                {pricingData.map((item, index) => (
                  <tr
                    key={index}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{item.zone}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 font-mono">{item.dimensions}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">{item.impressions}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {item.features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="text-xl font-bold text-emerald-600">{item.price}</div>
                      <div className="text-xs text-gray-500">per month</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pricing Notes */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-lg flex-shrink-0">
              <i className="ri-information-line text-xl"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Pricing Information</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                  <span>All prices are monthly with no long-term contracts required</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                  <span>Includes detailed analytics dashboard with real-time performance tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                  <span>Free ad creative design assistance and optimization recommendations</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                  <span>Multi-month discounts available: 10% off 3 months, 15% off 6 months, 20% off 12 months</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                  <span>Bundle multiple placements and save up to 25% on total cost</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}